/**
 * Centralized Configuration Management for LlamaSee Frontend
 *
 * This module provides a single source of truth for all configuration values.
 * Environment variables should be prefixed with REACT_APP_ to be accessible in React.
 */

import {
  STORAGE_KEYS,
  DEFAULT_RETURN_PATH,
  DEFAULT_OAUTH_DOMAIN,
  DEFAULT_LOCALHOST_API_PORT,
  DEFAULT_LOCALHOST_FRONTEND_PORT,
} from './constants';

// ========== Environment Detection ==========
const getEnvironment = () => {
  // Allow REACT_APP_ENVIRONMENT to override hostname detection
  // This allows running on localhost but connecting to dev-api/prod-api
  const envFromVar = process.env.REACT_APP_ENVIRONMENT;
  if (envFromVar && envFromVar !== 'localhost') {
    // If explicitly set to development/staging/production, use that
    return envFromVar;
  }

  const hostname = window.location.hostname;

  // If running on localhost and no explicit env var, show 'localhost'
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }

  // Fallback to development if no env var and not localhost
  return envFromVar || 'development';
};

const environment = getEnvironment();
const isProduction = environment === 'production';
const isDevelopment =
  environment === 'development' || environment === 'localhost';
const isStaging = environment === 'staging';

/**
 * Configuration validation error
 */
class ConfigurationError extends Error {
  constructor(message, missingValue) {
    super(message);
    this.name = 'ConfigurationError';
    this.missingValue = missingValue;
  }
}

/**
 * Validate required domain configuration with hostname-based fallbacks
 * Hostname-based detection allows the app to work even if env vars aren't set during build
 */
const validateDomainConfig = () => {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';

  // Detect domains from hostname if env vars are not set
  let domainProduction = process.env.REACT_APP_DOMAIN_PRODUCTION;
  let domainApi = process.env.REACT_APP_DOMAIN_API;
  let domainDevApi = process.env.REACT_APP_DOMAIN_DEV_API;
  let domainStagingApi = process.env.REACT_APP_DOMAIN_STAGING_API;

  // Hostname-based detection for production domain
  if (!domainProduction && hostname) {
    if (hostname === 'llamasee.com' || hostname.endsWith('.llamasee.com')) {
      domainProduction = 'llamasee.com';
    }
  }

  // Hostname-based detection for API domain
  if (!domainApi && hostname) {
    if (hostname === 'llamasee.com' || hostname.endsWith('.llamasee.com')) {
      domainApi = 'api.llamasee.com';
    }
  }

  // Hostname-based detection for dev API domain
  if (!domainDevApi && hostname) {
    if (hostname.includes('dev.') || hostname.includes('dev-')) {
      domainDevApi = 'dev-api.llamasee.com';
    }
  }

  // Hostname-based detection for staging API domain
  if (!domainStagingApi && hostname) {
    if (hostname.includes('staging.') || hostname.includes('staging-')) {
      domainStagingApi = 'staging-api.llamasee.com';
    }
  }

  // Validate required domains (production and API are always required)
  const required = {
    DOMAIN_PRODUCTION: domainProduction,
    DOMAIN_API: domainApi,
  };

  const missing = Object.entries(required)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    const errorMessage =
      `Missing required domain configuration: ${missing.join(', ')}. ` +
      'Hardcoded fallbacks are not allowed to prevent using wrong environment.';
    throw new ConfigurationError(errorMessage, missing);
  }

  // Return detected/configured values
  return {
    domainProduction,
    domainApi,
    domainDevApi,
    domainStagingApi,
  };
};

// ========== Domain Configuration ==========
// Validate and get domain values (with hostname-based fallbacks)
const domainConfig = validateDomainConfig();

const DOMAIN_PRODUCTION = domainConfig.domainProduction;
const DOMAIN_OAUTH = DEFAULT_OAUTH_DOMAIN; // From constants
const DOMAIN_API = domainConfig.domainApi;
const DOMAIN_DEV_API = domainConfig.domainDevApi;
const DOMAIN_STAGING_API = domainConfig.domainStagingApi;

// Local development ports - use constants from constants.js
const LOCALHOST_API_PORT =
  process.env.REACT_APP_API_PORT || DEFAULT_LOCALHOST_API_PORT;
const LOCALHOST_FRONTEND_PORT =
  process.env.REACT_APP_FRONTEND_PORT || DEFAULT_LOCALHOST_FRONTEND_PORT;

// ========== URL Builders ==========
/**
 * Get API base URL based on environment and hostname - NO hardcoded fallbacks
 * @throws {ConfigurationError} If required configuration is missing
 */
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const envFromVar = process.env.REACT_APP_ENVIRONMENT;

  // Priority 1: If actually running on localhost, always use localhost API
  // This allows local development without needing to change .env
  // REACT_APP_ENVIRONMENT is ignored when on localhost to ensure local development works automatically
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Always use localhost API when running on localhost
    return `http://localhost:${LOCALHOST_API_PORT}`;
  }

  // Priority 2: If REACT_APP_ENVIRONMENT is explicitly set (for deployed environments),
  // use that environment's API URL
  if (envFromVar === 'development') {
    const devApiDomain = DOMAIN_DEV_API || 'dev-api.llamasee.com';
    return `https://${devApiDomain}`;
  }

  if (envFromVar === 'staging') {
    const stagingApiDomain = DOMAIN_STAGING_API || 'staging-api.llamasee.com';
    return `https://${stagingApiDomain}`;
  }

  if (envFromVar === 'production') {
    return `https://${DOMAIN_API}`;
  }

  // Priority 3: Hostname-based detection (for deployed environments without explicit env var)
  if (hostname.includes('dev.') || hostname.includes('dev-')) {
    // Use detected domain or fallback to standard dev API domain
    const devApiDomain = DOMAIN_DEV_API || 'dev-api.llamasee.com';
    return `https://${devApiDomain}`;
  }

  if (hostname.includes('staging.') || hostname.includes('staging-')) {
    // Use detected domain or fallback to standard staging API domain
    const stagingApiDomain = DOMAIN_STAGING_API || 'staging-api.llamasee.com';
    return `https://${stagingApiDomain}`;
  }

  // Priority 4: Production fallback - DOMAIN_API is already validated in validateDomainConfig()
  return `https://${DOMAIN_API}`;
};

/**
 * Get SSO login URL
 */
const getSSOLoginUrl = () => {
  const apiBase = getApiBaseUrl();
  return `${apiBase}/auth/sso/login`;
};

/**
 * Get SSO token exchange URL
 */
const getSSOExchangeUrl = () => {
  const apiBase = getApiBaseUrl();
  return `${apiBase}/auth/sso/exchange-token`;
};

/**
 * Get tenant-specific base URL - NO hardcoded fallbacks
 * @throws {ConfigurationError} If required configuration is missing
 * @deprecated This function is not currently used. Kept for potential future use.
 */
// eslint-disable-next-line no-unused-vars
const getTenantBaseUrl = tenantAlias => {
  if (!tenantAlias) {
    const errorMessage = 'Tenant alias is required for getTenantBaseUrl';
    throw new ConfigurationError(errorMessage, 'tenant_alias');
  }

  if (isProduction) {
    // DOMAIN_PRODUCTION is already validated
    return `https://${tenantAlias}.${DOMAIN_PRODUCTION}`;
  }

  // In development, use localhost with default port from constants
  return `http://localhost:${LOCALHOST_FRONTEND_PORT}`;
};

// ========== Tenant Detection ==========
/**
 * Extract tenant alias from URL
 */
const getTenantFromUrl = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Check if running on localhost - get tenant from URL params or path
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check URL search params first
    const urlParams = new URLSearchParams(window.location.search);
    const tenantFromParam = urlParams.get('tenant');
    if (tenantFromParam) {
      return tenantFromParam;
    }

    // Check if tenant is in path (e.g., /tenant/tenant_alias/login)
    const pathParts = pathname.split('/').filter(part => part);
    if (pathParts.length >= 2 && pathParts[0] === 'tenant') {
      return pathParts[1];
    }

    // No fallback - return null if tenant not found in URL
    return null;
  }

  // Production: extract from subdomain (e.g., tenant.llamasee.com)
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[1] === 'llamasee' && parts[2] === 'com') {
    return parts[0]; // tenant part
  }

  // No fallback - return null if tenant not found
  return null;
};

// Get default tenant alias dynamically (not cached at module load)
// Get default tenant alias from environment variable or config
// For production, returns null (must be in subdomain or URL param)
// For localhost, can use REACT_APP_DEFAULT_TENANT if set
const getDefaultTenantAlias = () => {
  const tenantFromUrl = getTenantFromUrl();
  if (tenantFromUrl) {
    return tenantFromUrl;
  }

  // For localhost, check if default tenant is configured via environment variable
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Use environment variable if set, otherwise return null (no default)
      const defaultTenant = process.env.REACT_APP_DEFAULT_TENANT;
      if (defaultTenant) {
        return defaultTenant;
      }
    }
  }

  return null;
};

// DEFAULT_RETURN_PATH is imported from constants.js

// ========== LocalStorage Keys ==========
// STORAGE_KEYS is imported from constants.js

// ========== Tenant Display Names ==========
/**
 * Get tenant display name from backend API
 */
export const getTenantDisplayName = async tenantAlias => {
  if (!tenantAlias) return 'Unknown Tenant';

  try {
    const apiBase = getApiBaseUrl();
    const response = await fetch(
      `${apiBase}/admin/tenant/info?alias=${tenantAlias}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.ok) {
      const data = await response.json();
      return data.tenant_name || tenantAlias;
    }
  } catch (error) {
    // Failed to fetch tenant info
  }

  // Fallback to a formatted version of the alias
  return tenantAlias.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// ========== Configuration Object ==========
const config = {
  // Environment
  environment,
  isProduction,
  isDevelopment,
  isStaging,

  // Domains
  domains: {
    production: DOMAIN_PRODUCTION,
    oauth: DOMAIN_OAUTH,
    api: DOMAIN_API,
    devApi: DOMAIN_DEV_API,
    stagingApi: DOMAIN_STAGING_API,
  },

  // Ports (for debugging/development tools)
  ports: {
    api: LOCALHOST_API_PORT,
    frontend: LOCALHOST_FRONTEND_PORT,
  },

  // URLs (using getters to ensure dynamic evaluation)
  urls: {
    get apiBase() {
      return getApiBaseUrl();
    },
    get ssoLogin() {
      return getSSOLoginUrl();
    },
    get ssoExchange() {
      return getSSOExchangeUrl();
    },
  },

  // Defaults
  defaults: {
    // Use getter to ensure tenantAlias is evaluated dynamically
    get tenantAlias() {
      return getDefaultTenantAlias();
    },
    returnPath: DEFAULT_RETURN_PATH,
  },

  // Storage keys
  storage: STORAGE_KEYS,

  // Helper functions (useful for dynamic URL generation)
  getApiBaseUrl,
  getSSOLoginUrl,
  getSSOExchangeUrl,
};

// ========== Development Logging ==========
if (isDevelopment) {
  // Auto-load test utility in development (defer to avoid circular dependencies)
  if (typeof window !== 'undefined') {
    // Use setTimeout to ensure config is fully initialized first
    setTimeout(() => {
      import('../utils/testEnvConfig.js')
        .then(testUtils => {
          window.testEnvConfig = testUtils;
        })
        .catch(() => {
          // Silently fail if test utils can't be loaded
        });
    }, 100);
  }
}

// ========== Validation ==========
if (isProduction) {
  // Note: These environment variables have sensible defaults, so they're not truly required
  // However, it's good practice to set them explicitly in production deployments
  // Recommended environment variables not set (using defaults)
}

export default config;
